/**
 * Single source of truth: UI settings → Bambu/Orca project_settings.config keys.
 * Only keys confirmed present in Orca BBL / Bambu P1S profiles.
 */

export type Visibility = 'basic' | 'advanced' | 'expert';
export type SettingType = 'number' | 'integer' | 'percent' | 'boolean' | 'enum' | 'string' | 'color';

export interface SettingDef {
  id: string;
  /** Real key in project_settings.config / processSettings */
  key: string;
  label: string;
  category: string;
  visibility: Visibility;
  type: SettingType;
  default: string | number | boolean;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Enum options: value is what goes into the config */
  options?: { value: string; label: string }[];
  /** If true, value is stored as array in config (filament-scoped) */
  array?: boolean;
  /** Hint text */
  description?: string;
  /** Can be overridden per-object in model_settings.config */
  perObject?: boolean;
  /** Format helper: percent keys often need "15%" string */
  asPercentString?: boolean;
}

export const CATEGORIES = [
  'Quality',
  'Walls',
  'Top/Bottom',
  'Infill',
  'Material',
  'Speed',
  'Travel',
  'Cooling',
  'Supports',
  'Adhesion',
  'Special',
  'Ironing',
  'Fuzzy Skin',
] as const;

export type Category = (typeof CATEGORIES)[number];

const enumOpts = (pairs: [string, string][]) =>
  pairs.map(([value, label]) => ({ value, label }));

export const SETTINGS: SettingDef[] = [
  // ——— Quality ———
  {
    id: 'layer_height',
    key: 'layer_height',
    label: 'Layer height',
    category: 'Quality',
    visibility: 'basic',
    type: 'number',
    default: 0.2,
    unit: 'mm',
    min: 0.08,
    max: 0.28,
    step: 0.01,
    perObject: true,
    description: 'Layer height for the print',
  },
  {
    id: 'initial_layer_print_height',
    key: 'initial_layer_print_height',
    label: 'First layer height',
    category: 'Quality',
    visibility: 'basic',
    type: 'number',
    default: 0.2,
    unit: 'mm',
    min: 0.08,
    max: 0.35,
    step: 0.01,
  },
  {
    id: 'outer_wall_line_width',
    key: 'outer_wall_line_width',
    label: 'Outer wall line width',
    category: 'Quality',
    visibility: 'advanced',
    type: 'number',
    default: 0.42,
    unit: 'mm',
    min: 0.2,
    max: 0.6,
    step: 0.01,
  },
  {
    id: 'inner_wall_line_width',
    key: 'inner_wall_line_width',
    label: 'Inner wall line width',
    category: 'Quality',
    visibility: 'advanced',
    type: 'number',
    default: 0.45,
    unit: 'mm',
    min: 0.2,
    max: 0.6,
    step: 0.01,
  },
  {
    id: 'sparse_infill_line_width',
    key: 'sparse_infill_line_width',
    label: 'Infill line width',
    category: 'Quality',
    visibility: 'expert',
    type: 'number',
    default: 0.45,
    unit: 'mm',
    min: 0.2,
    max: 0.6,
    step: 0.01,
  },
  {
    id: 'elefant_foot_compensation',
    key: 'elefant_foot_compensation',
    label: 'Elephant foot compensation',
    category: 'Quality',
    visibility: 'advanced',
    type: 'number',
    default: 0.15,
    unit: 'mm',
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    id: 'resolution',
    key: 'resolution',
    label: 'Resolution',
    category: 'Quality',
    visibility: 'expert',
    type: 'number',
    default: 0.012,
    unit: 'mm',
    min: 0.001,
    max: 0.05,
    step: 0.001,
  },

  // ——— Walls ———
  {
    id: 'wall_loops',
    key: 'wall_loops',
    label: 'Wall loops',
    category: 'Walls',
    visibility: 'basic',
    type: 'integer',
    default: 2,
    min: 0,
    max: 20,
    step: 1,
    perObject: true,
  },
  {
    id: 'wall_infill_order',
    key: 'wall_infill_order',
    label: 'Wall/infill order',
    category: 'Walls',
    visibility: 'advanced',
    type: 'enum',
    default: 'inner wall/outer wall/infill',
    options: enumOpts([
      ['inner wall/outer wall/infill', 'Inner → Outer → Infill'],
      ['outer wall/inner wall/infill', 'Outer → Inner → Infill'],
      ['infill/inner wall/outer wall', 'Infill → Inner → Outer'],
      ['infill/outer wall/inner wall', 'Infill → Outer → Inner'],
      ['inner wall/infill/outer wall', 'Inner → Infill → Outer'],
    ]),
  },
  {
    id: 'seam_position',
    key: 'seam_position',
    label: 'Seam position',
    category: 'Walls',
    visibility: 'basic',
    type: 'enum',
    default: 'aligned',
    options: enumOpts([
      ['aligned', 'Aligned'],
      ['nearest', 'Nearest'],
      ['back', 'Back'],
      ['random', 'Random'],
    ]),
  },
  {
    id: 'detect_thin_wall',
    key: 'detect_thin_wall',
    label: 'Detect thin walls',
    category: 'Walls',
    visibility: 'advanced',
    type: 'boolean',
    default: false,
  },
  {
    id: 'detect_overhang_wall',
    key: 'detect_overhang_wall',
    label: 'Detect overhang walls',
    category: 'Walls',
    visibility: 'advanced',
    type: 'boolean',
    default: true,
  },

  // ——— Top/Bottom ———
  {
    id: 'top_shell_layers',
    key: 'top_shell_layers',
    label: 'Top shell layers',
    category: 'Top/Bottom',
    visibility: 'basic',
    type: 'integer',
    default: 5,
    min: 0,
    max: 50,
    step: 1,
    perObject: true,
  },
  {
    id: 'top_shell_thickness',
    key: 'top_shell_thickness',
    label: 'Top shell thickness',
    category: 'Top/Bottom',
    visibility: 'advanced',
    type: 'number',
    default: 1.0,
    unit: 'mm',
    min: 0,
    max: 10,
    step: 0.1,
  },
  {
    id: 'bottom_shell_layers',
    key: 'bottom_shell_layers',
    label: 'Bottom shell layers',
    category: 'Top/Bottom',
    visibility: 'basic',
    type: 'integer',
    default: 3,
    min: 0,
    max: 50,
    step: 1,
    perObject: true,
  },
  {
    id: 'bottom_shell_thickness',
    key: 'bottom_shell_thickness',
    label: 'Bottom shell thickness',
    category: 'Top/Bottom',
    visibility: 'advanced',
    type: 'number',
    default: 0,
    unit: 'mm',
    min: 0,
    max: 10,
    step: 0.1,
  },
  {
    id: 'top_surface_pattern',
    key: 'top_surface_pattern',
    label: 'Top surface pattern',
    category: 'Top/Bottom',
    visibility: 'advanced',
    type: 'enum',
    default: 'monotonicline',
    options: enumOpts([
      ['monotonic', 'Monotonic'],
      ['monotonicline', 'Monotonic line'],
      ['concentric', 'Concentric'],
      ['rectilinear', 'Rectilinear'],
      ['hilbertcurve', 'Hilbert'],
      ['archimedeanchords', 'Archimedean chords'],
      ['octagramspiral', 'Octagram spiral'],
    ]),
  },
  {
    id: 'bottom_surface_pattern',
    key: 'bottom_surface_pattern',
    label: 'Bottom surface pattern',
    category: 'Top/Bottom',
    visibility: 'advanced',
    type: 'enum',
    default: 'monotonic',
    options: enumOpts([
      ['monotonic', 'Monotonic'],
      ['monotonicline', 'Monotonic line'],
      ['concentric', 'Concentric'],
      ['rectilinear', 'Rectilinear'],
      ['hilbertcurve', 'Hilbert'],
    ]),
  },

  // ——— Infill ———
  {
    id: 'sparse_infill_density',
    key: 'sparse_infill_density',
    label: 'Infill density',
    category: 'Infill',
    visibility: 'basic',
    type: 'percent',
    default: 15,
    unit: '%',
    min: 0,
    max: 100,
    step: 1,
    asPercentString: true,
    perObject: true,
  },
  {
    id: 'sparse_infill_pattern',
    key: 'sparse_infill_pattern',
    label: 'Infill pattern',
    category: 'Infill',
    visibility: 'basic',
    type: 'enum',
    default: 'crosshatch',
    options: enumOpts([
      ['crosshatch', 'Crosshatch'],
      ['grid', 'Grid'],
      ['gyroid', 'Gyroid'],
      ['cubic', 'Cubic'],
      ['lightning', 'Lightning'],
      ['honeycomb', 'Honeycomb'],
      ['triangles', 'Triangles'],
      ['trihexagon', 'Tri-hexagon'],
      ['line', 'Line'],
      ['concentric', 'Concentric'],
      ['zigzag', 'Zigzag'],
      ['3dhoneycomb', '3D honeycomb'],
      ['adaptivecubic', 'Adaptive cubic'],
      ['supportcubic', 'Support cubic'],
    ]),
    perObject: true,
  },
  {
    id: 'infill_direction',
    key: 'infill_direction',
    label: 'Infill direction',
    category: 'Infill',
    visibility: 'advanced',
    type: 'number',
    default: 45,
    unit: '°',
    min: 0,
    max: 360,
    step: 5,
  },
  {
    id: 'infill_wall_overlap',
    key: 'infill_wall_overlap',
    label: 'Infill/wall overlap',
    category: 'Infill',
    visibility: 'advanced',
    type: 'percent',
    default: 15,
    unit: '%',
    min: 0,
    max: 50,
    step: 1,
    asPercentString: true,
  },
  {
    id: 'minimum_sparse_infill_area',
    key: 'minimum_sparse_infill_area',
    label: 'Min sparse infill area',
    category: 'Infill',
    visibility: 'expert',
    type: 'number',
    default: 15,
    unit: 'mm²',
    min: 0,
    max: 100,
    step: 1,
  },
  {
    id: 'infill_anchor',
    key: 'infill_anchor',
    label: 'Infill anchor',
    category: 'Infill',
    visibility: 'expert',
    type: 'number',
    default: 2.5,
    unit: 'mm',
    min: 0,
    max: 20,
    step: 0.5,
  },
  {
    id: 'infill_anchor_max',
    key: 'infill_anchor_max',
    label: 'Infill anchor max',
    category: 'Infill',
    visibility: 'expert',
    type: 'number',
    default: 12,
    unit: 'mm',
    min: 0,
    max: 100,
    step: 1,
  },

  // ——— Material ———
  {
    id: 'filament_type',
    key: 'filament_type',
    label: 'Filament type',
    category: 'Material',
    visibility: 'basic',
    type: 'enum',
    default: 'PLA',
    array: true,
    options: enumOpts([
      ['PLA', 'PLA'],
      ['PETG', 'PETG'],
      ['TPU', 'TPU'],
      ['ABS', 'ABS'],
      ['ASA', 'ASA'],
      ['PC', 'PC'],
      ['PA', 'PA'],
      ['PLA-CF', 'PLA-CF'],
      ['PETG-CF', 'PETG-CF'],
    ]),
  },
  {
    id: 'nozzle_temperature',
    key: 'nozzle_temperature',
    label: 'Nozzle temperature',
    category: 'Material',
    visibility: 'basic',
    type: 'integer',
    default: 220,
    unit: '°C',
    min: 150,
    max: 300,
    step: 1,
    array: true,
  },
  {
    id: 'nozzle_temperature_initial_layer',
    key: 'nozzle_temperature_initial_layer',
    label: 'First layer nozzle temp',
    category: 'Material',
    visibility: 'advanced',
    type: 'integer',
    default: 220,
    unit: '°C',
    min: 150,
    max: 300,
    step: 1,
    array: true,
  },
  {
    id: 'filament_flow_ratio',
    key: 'filament_flow_ratio',
    label: 'Flow ratio',
    category: 'Material',
    visibility: 'advanced',
    type: 'number',
    default: 0.98,
    min: 0.8,
    max: 1.2,
    step: 0.01,
    array: true,
  },
  {
    id: 'filament_density',
    key: 'filament_density',
    label: 'Filament density',
    category: 'Material',
    visibility: 'expert',
    type: 'number',
    default: 1.26,
    unit: 'g/cm³',
    min: 0.5,
    max: 3,
    step: 0.01,
    array: true,
  },
  {
    id: 'filament_diameter',
    key: 'filament_diameter',
    label: 'Filament diameter',
    category: 'Material',
    visibility: 'expert',
    type: 'number',
    default: 1.75,
    unit: 'mm',
    min: 1.5,
    max: 3.0,
    step: 0.05,
    array: true,
  },
  {
    id: 'filament_colour',
    key: 'filament_colour',
    label: 'Filament colour',
    category: 'Material',
    visibility: 'basic',
    type: 'color',
    default: '#00AE42',
    array: true,
  },
  {
    id: 'filament_max_volumetric_speed',
    key: 'filament_max_volumetric_speed',
    label: 'Max volumetric speed',
    category: 'Material',
    visibility: 'expert',
    type: 'number',
    default: 21,
    unit: 'mm³/s',
    min: 1,
    max: 50,
    step: 0.5,
    array: true,
  },

  // ——— Speed ———
  {
    id: 'outer_wall_speed',
    key: 'outer_wall_speed',
    label: 'Outer wall speed',
    category: 'Speed',
    visibility: 'basic',
    type: 'integer',
    default: 200,
    unit: 'mm/s',
    min: 10,
    max: 500,
    step: 5,
    array: true,
  },
  {
    id: 'inner_wall_speed',
    key: 'inner_wall_speed',
    label: 'Inner wall speed',
    category: 'Speed',
    visibility: 'basic',
    type: 'integer',
    default: 300,
    unit: 'mm/s',
    min: 10,
    max: 500,
    step: 5,
    array: true,
  },
  {
    id: 'sparse_infill_speed',
    key: 'sparse_infill_speed',
    label: 'Sparse infill speed',
    category: 'Speed',
    visibility: 'basic',
    type: 'integer',
    default: 270,
    unit: 'mm/s',
    min: 10,
    max: 500,
    step: 5,
    array: true,
  },
  {
    id: 'internal_solid_infill_speed',
    key: 'internal_solid_infill_speed',
    label: 'Solid infill speed',
    category: 'Speed',
    visibility: 'advanced',
    type: 'integer',
    default: 250,
    unit: 'mm/s',
    min: 10,
    max: 500,
    step: 5,
    array: true,
  },
  {
    id: 'top_surface_speed',
    key: 'top_surface_speed',
    label: 'Top surface speed',
    category: 'Speed',
    visibility: 'advanced',
    type: 'integer',
    default: 200,
    unit: 'mm/s',
    min: 10,
    max: 500,
    step: 5,
    array: true,
  },
  {
    id: 'initial_layer_speed',
    key: 'initial_layer_speed',
    label: 'First layer speed',
    category: 'Speed',
    visibility: 'basic',
    type: 'integer',
    default: 50,
    unit: 'mm/s',
    min: 5,
    max: 150,
    step: 5,
    array: true,
  },
  {
    id: 'support_speed',
    key: 'support_speed',
    label: 'Support speed',
    category: 'Speed',
    visibility: 'advanced',
    type: 'integer',
    default: 150,
    unit: 'mm/s',
    min: 10,
    max: 300,
    step: 5,
    array: true,
  },

  // ——— Travel ———
  {
    id: 'travel_speed',
    key: 'travel_speed',
    label: 'Travel speed',
    category: 'Travel',
    visibility: 'basic',
    type: 'integer',
    default: 500,
    unit: 'mm/s',
    min: 50,
    max: 700,
    step: 10,
    array: true,
  },
  {
    id: 'retraction_length',
    key: 'retraction_length',
    label: 'Retraction length',
    category: 'Travel',
    visibility: 'basic',
    type: 'number',
    default: 0.8,
    unit: 'mm',
    min: 0,
    max: 10,
    step: 0.1,
    array: true,
  },
  {
    id: 'retraction_speed',
    key: 'retraction_speed',
    label: 'Retraction speed',
    category: 'Travel',
    visibility: 'advanced',
    type: 'integer',
    default: 30,
    unit: 'mm/s',
    min: 5,
    max: 100,
    step: 1,
    array: true,
  },
  {
    id: 'deretraction_speed',
    key: 'deretraction_speed',
    label: 'Deretraction speed',
    category: 'Travel',
    visibility: 'expert',
    type: 'integer',
    default: 30,
    unit: 'mm/s',
    min: 5,
    max: 100,
    step: 1,
    array: true,
  },
  {
    id: 'retraction_minimum_travel',
    key: 'retraction_minimum_travel',
    label: 'Min travel for retract',
    category: 'Travel',
    visibility: 'expert',
    type: 'number',
    default: 1,
    unit: 'mm',
    min: 0,
    max: 20,
    step: 0.5,
    array: true,
  },
  {
    id: 'z_hop',
    key: 'z_hop',
    label: 'Z hop',
    category: 'Travel',
    visibility: 'advanced',
    type: 'number',
    default: 0.4,
    unit: 'mm',
    min: 0,
    max: 5,
    step: 0.1,
    array: true,
  },
  {
    id: 'z_hop_types',
    key: 'z_hop_types',
    label: 'Z hop type',
    category: 'Travel',
    visibility: 'advanced',
    type: 'enum',
    default: 'Slope',
    array: true,
    options: enumOpts([
      ['Normal', 'Normal'],
      ['Slope', 'Slope'],
      ['Spiral', 'Spiral'],
      ['Auto', 'Auto'],
    ]),
  },

  // ——— Cooling ———
  {
    id: 'fan_min_speed',
    key: 'fan_min_speed',
    label: 'Min fan speed',
    category: 'Cooling',
    visibility: 'basic',
    type: 'integer',
    default: 50,
    unit: '%',
    min: 0,
    max: 100,
    step: 5,
    array: true,
  },
  {
    id: 'fan_max_speed',
    key: 'fan_max_speed',
    label: 'Max fan speed',
    category: 'Cooling',
    visibility: 'basic',
    type: 'integer',
    default: 100,
    unit: '%',
    min: 0,
    max: 100,
    step: 5,
    array: true,
  },
  {
    id: 'fan_cooling_layer_time',
    key: 'fan_cooling_layer_time',
    label: 'Layer time for cooling',
    category: 'Cooling',
    visibility: 'advanced',
    type: 'integer',
    default: 80,
    unit: 's',
    min: 0,
    max: 300,
    step: 5,
    array: true,
  },
  {
    id: 'close_fan_the_first_x_layers',
    key: 'close_fan_the_first_x_layers',
    label: 'Keep fan off first N layers',
    category: 'Cooling',
    visibility: 'advanced',
    type: 'integer',
    default: 1,
    min: 0,
    max: 20,
    step: 1,
    array: true,
  },
  {
    id: 'overhang_fan_speed',
    key: 'overhang_fan_speed',
    label: 'Overhang fan speed',
    category: 'Cooling',
    visibility: 'advanced',
    type: 'integer',
    default: 100,
    unit: '%',
    min: 0,
    max: 100,
    step: 5,
    array: true,
  },
  {
    id: 'reduce_fan_stop_start_freq',
    key: 'reduce_fan_stop_start_freq',
    label: 'Keep fan always on',
    category: 'Cooling',
    visibility: 'expert',
    type: 'boolean',
    default: true,
    array: true,
  },

  // ——— Supports ———
  {
    id: 'enable_support',
    key: 'enable_support',
    label: 'Enable supports',
    category: 'Supports',
    visibility: 'basic',
    type: 'boolean',
    default: false,
    perObject: true,
    description: 'For painted supports use tree(manual) + enable=on (T5 fix)',
  },
  {
    id: 'support_type',
    key: 'support_type',
    label: 'Support type',
    category: 'Supports',
    visibility: 'basic',
    type: 'enum',
    default: 'tree(auto)',
    options: enumOpts([
      ['normal(auto)', 'Normal (auto)'],
      ['tree(auto)', 'Tree (auto)'],
      ['normal(manual)', 'Normal (manual)'],
      ['tree(manual)', 'Tree (manual)'],
    ]),
    perObject: true,
  },
  {
    id: 'support_threshold_angle',
    key: 'support_threshold_angle',
    label: 'Overhang threshold',
    category: 'Supports',
    visibility: 'basic',
    type: 'integer',
    default: 30,
    unit: '°',
    min: 0,
    max: 90,
    step: 1,
  },
  {
    id: 'support_on_build_plate_only',
    key: 'support_on_build_plate_only',
    label: 'Build plate only',
    category: 'Supports',
    visibility: 'basic',
    type: 'boolean',
    default: false,
  },
  {
    id: 'support_base_pattern',
    key: 'support_base_pattern',
    label: 'Support base pattern',
    category: 'Supports',
    visibility: 'advanced',
    type: 'enum',
    default: 'default',
    options: enumOpts([
      ['default', 'Default'],
      ['rectilinear', 'Rectilinear'],
      ['rectilinear grid', 'Rectilinear grid'],
      ['honeycomb', 'Honeycomb'],
      ['lightning', 'Lightning'],
      ['none', 'None'],
    ]),
  },
  {
    id: 'support_base_pattern_spacing',
    key: 'support_base_pattern_spacing',
    label: 'Support density spacing',
    category: 'Supports',
    visibility: 'advanced',
    type: 'number',
    default: 2.5,
    unit: 'mm',
    min: 0.5,
    max: 10,
    step: 0.1,
  },
  {
    id: 'support_top_z_distance',
    key: 'support_top_z_distance',
    label: 'Top Z distance',
    category: 'Supports',
    visibility: 'advanced',
    type: 'number',
    default: 0.2,
    unit: 'mm',
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    id: 'support_bottom_z_distance',
    key: 'support_bottom_z_distance',
    label: 'Bottom Z distance',
    category: 'Supports',
    visibility: 'advanced',
    type: 'number',
    default: 0.2,
    unit: 'mm',
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    id: 'support_interface_top_layers',
    key: 'support_interface_top_layers',
    label: 'Interface top layers',
    category: 'Supports',
    visibility: 'advanced',
    type: 'integer',
    default: 2,
    min: 0,
    max: 10,
    step: 1,
  },
  {
    id: 'support_interface_bottom_layers',
    key: 'support_interface_bottom_layers',
    label: 'Interface bottom layers',
    category: 'Supports',
    visibility: 'expert',
    type: 'integer',
    default: 2,
    min: 0,
    max: 10,
    step: 1,
  },
  {
    id: 'support_interface_pattern',
    key: 'support_interface_pattern',
    label: 'Interface pattern',
    category: 'Supports',
    visibility: 'expert',
    type: 'enum',
    default: 'auto',
    options: enumOpts([
      ['auto', 'Auto'],
      ['rectilinear', 'Rectilinear'],
      ['concentric', 'Concentric'],
      ['rectilinear grid', 'Rectilinear grid'],
      ['none', 'None'],
    ]),
  },
  {
    id: 'support_interface_spacing',
    key: 'support_interface_spacing',
    label: 'Interface spacing',
    category: 'Supports',
    visibility: 'expert',
    type: 'number',
    default: 0.5,
    unit: 'mm',
    min: 0,
    max: 5,
    step: 0.1,
  },
  {
    id: 'support_object_xy_distance',
    key: 'support_object_xy_distance',
    label: 'XY distance',
    category: 'Supports',
    visibility: 'advanced',
    type: 'number',
    default: 0.35,
    unit: 'mm',
    min: 0,
    max: 2,
    step: 0.05,
  },
  {
    id: 'tree_support_branch_angle',
    key: 'tree_support_branch_angle',
    label: 'Tree branch angle',
    category: 'Supports',
    visibility: 'advanced',
    type: 'integer',
    default: 45,
    unit: '°',
    min: 0,
    max: 80,
    step: 1,
  },
  {
    id: 'tree_support_branch_diameter',
    key: 'tree_support_branch_diameter',
    label: 'Tree branch diameter',
    category: 'Supports',
    visibility: 'advanced',
    type: 'number',
    default: 2,
    unit: 'mm',
    min: 0.5,
    max: 10,
    step: 0.1,
  },
  {
    id: 'bridge_no_support',
    key: 'bridge_no_support',
    label: 'Do not support bridges',
    category: 'Supports',
    visibility: 'expert',
    type: 'boolean',
    default: false,
  },

  // ——— Adhesion ———
  {
    id: 'brim_type',
    key: 'brim_type',
    label: 'Brim type',
    category: 'Adhesion',
    visibility: 'basic',
    type: 'enum',
    default: 'auto_brim',
    options: enumOpts([
      ['auto_brim', 'Auto'],
      ['brim_ears', 'Brim ears'],
      ['outer_only', 'Outer only'],
      ['inner_only', 'Inner only'],
      ['outer_and_inner', 'Outer + inner'],
      ['no_brim', 'No brim'],
    ]),
  },
  {
    id: 'brim_width',
    key: 'brim_width',
    label: 'Brim width',
    category: 'Adhesion',
    visibility: 'basic',
    type: 'number',
    default: 5,
    unit: 'mm',
    min: 0,
    max: 50,
    step: 0.5,
  },
  {
    id: 'brim_object_gap',
    key: 'brim_object_gap',
    label: 'Brim-object gap',
    category: 'Adhesion',
    visibility: 'advanced',
    type: 'number',
    default: 0.1,
    unit: 'mm',
    min: 0,
    max: 2,
    step: 0.05,
  },
  {
    id: 'skirt_loops',
    key: 'skirt_loops',
    label: 'Skirt loops',
    category: 'Adhesion',
    visibility: 'basic',
    type: 'integer',
    default: 0,
    min: 0,
    max: 20,
    step: 1,
  },
  {
    id: 'skirt_distance',
    key: 'skirt_distance',
    label: 'Skirt distance',
    category: 'Adhesion',
    visibility: 'advanced',
    type: 'number',
    default: 2,
    unit: 'mm',
    min: 0,
    max: 20,
    step: 0.5,
  },
  {
    id: 'skirt_height',
    key: 'skirt_height',
    label: 'Skirt height',
    category: 'Adhesion',
    visibility: 'expert',
    type: 'integer',
    default: 1,
    min: 1,
    max: 10,
    step: 1,
  },
  {
    id: 'raft_layers',
    key: 'raft_layers',
    label: 'Raft layers',
    category: 'Adhesion',
    visibility: 'basic',
    type: 'integer',
    default: 0,
    min: 0,
    max: 10,
    step: 1,
  },

  // ——— Special ———
  {
    id: 'spiral_mode',
    key: 'spiral_mode',
    label: 'Spiral vase mode',
    category: 'Special',
    visibility: 'basic',
    type: 'boolean',
    default: false,
  },
  {
    id: 'print_sequence',
    key: 'print_sequence',
    label: 'Print sequence',
    category: 'Special',
    visibility: 'advanced',
    type: 'enum',
    default: 'by layer',
    options: enumOpts([
      ['by layer', 'By layer'],
      ['by object', 'By object'],
    ]),
  },
  {
    id: 'reduce_crossing_wall',
    key: 'reduce_crossing_wall',
    label: 'Avoid crossing walls',
    category: 'Special',
    visibility: 'advanced',
    type: 'boolean',
    default: false,
  },
  {
    id: 'enable_arc_fitting',
    key: 'enable_arc_fitting',
    label: 'Arc fitting',
    category: 'Special',
    visibility: 'expert',
    type: 'boolean',
    default: true,
  },

  // ——— Ironing ———
  {
    id: 'ironing_type',
    key: 'ironing_type',
    label: 'Ironing',
    category: 'Ironing',
    visibility: 'basic',
    type: 'enum',
    default: 'no ironing',
    options: enumOpts([
      ['no ironing', 'Off'],
      ['top', 'Top surfaces'],
      ['topmost', 'Topmost surface'],
      ['solid', 'All solid'],
    ]),
  },
  {
    id: 'ironing_flow',
    key: 'ironing_flow',
    label: 'Ironing flow',
    category: 'Ironing',
    visibility: 'advanced',
    type: 'percent',
    default: 10,
    unit: '%',
    min: 0,
    max: 100,
    step: 1,
    asPercentString: true,
  },
  {
    id: 'ironing_spacing',
    key: 'ironing_spacing',
    label: 'Ironing spacing',
    category: 'Ironing',
    visibility: 'advanced',
    type: 'number',
    default: 0.15,
    unit: 'mm',
    min: 0.05,
    max: 1,
    step: 0.01,
  },
  {
    id: 'ironing_speed',
    key: 'ironing_speed',
    label: 'Ironing speed',
    category: 'Ironing',
    visibility: 'advanced',
    type: 'integer',
    default: 30,
    unit: 'mm/s',
    min: 5,
    max: 100,
    step: 1,
  },

  // ——— Fuzzy Skin ———
  {
    id: 'fuzzy_skin',
    key: 'fuzzy_skin',
    label: 'Fuzzy skin',
    category: 'Fuzzy Skin',
    visibility: 'basic',
    type: 'enum',
    default: 'none',
    options: enumOpts([
      ['none', 'Off'],
      ['external', 'External walls'],
      ['all', 'All walls'],
      ['contour', 'Contour'],
      ['hole', 'Holes only'],
    ]),
  },
  {
    id: 'fuzzy_skin_thickness',
    key: 'fuzzy_skin_thickness',
    label: 'Fuzzy thickness',
    category: 'Fuzzy Skin',
    visibility: 'advanced',
    type: 'number',
    default: 0.3,
    unit: 'mm',
    min: 0.05,
    max: 1,
    step: 0.05,
  },
  {
    id: 'fuzzy_skin_point_distance',
    key: 'fuzzy_skin_point_distance',
    label: 'Fuzzy point distance',
    category: 'Fuzzy Skin',
    visibility: 'advanced',
    type: 'number',
    default: 0.8,
    unit: 'mm',
    min: 0.1,
    max: 5,
    step: 0.1,
  },
];

/** Bed size / machine constants for P1S */
export const P1S = {
  bedX: 256,
  bedY: 256,
  bedZ: 256,
  nozzle: 0.4,
  name: 'Bambu Lab P1S 0.4 nozzle',
} as const;

export function defaultsFromSchema(): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const s of SETTINGS) out[s.id] = s.default;
  return out;
}

export function settingsVisible(
  level: Visibility,
  s: SettingDef
): boolean {
  const order: Visibility[] = ['basic', 'advanced', 'expert'];
  return order.indexOf(s.visibility) <= order.indexOf(level);
}

/** Serialise a UI value into the string/array form processSettings / config expects */
export function toConfigValue(
  def: SettingDef,
  value: string | number | boolean
): string | string[] | number | boolean {
  let v: string | number | boolean = value;
  if (def.type === 'boolean') {
    // bambu-3mf serializeSettings maps bool → "0"/"1"; pass bool for processSettings
    v = Boolean(value);
  } else if (def.asPercentString) {
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    v = `${n}%`;
  } else if (def.type === 'integer' || def.type === 'number' || def.type === 'percent') {
    v = typeof value === 'number' ? value : Number(value);
  } else {
    v = String(value);
  }
  if (def.array) {
    return [String(typeof v === 'boolean' ? (v ? '1' : '0') : v)];
  }
  return v;
}

export function buildProcessSettings(
  values: Record<string, string | number | boolean>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const def of SETTINGS) {
    if (!(def.id in values)) continue;
    out[def.key] = toConfigValue(def, values[def.id]);
  }
  // T5 painted-supports fix is applied by the exporter when paint is present
  return out;
}

export const PER_OBJECT_KEYS = SETTINGS.filter((s) => s.perObject).map((s) => s.key);
